import React from 'react'
import { useSWRConfig } from 'swr'

import {
  DASHBOARD_TAB,
  CATEGORY_TAB,
  ALL_TAB,
  CARDS_TAB,
  useTaskPageState
} from '../TaskPageStateProvider'
import { useTaskCardState } from '../TaskCardStateProvider'

type TaskSWRMutateCacheContextType = {
  updateCardChange: (oldTask: APITask, newTask: APITask) => void
  updateTasks: (tab: number, task: APITask) => void
  updateCategories: () => void
  setMutator: (tab: number, key: string, mutator: () => void) => void
}

const TaskSWRMutateCacheContext =
  React.createContext<TaskSWRMutateCacheContextType | null>(null)

type CacheType = {
  [tab: number]: {
    [id: string]: () => void
  }
}

const TaskSWRMutateCacheProvider = ({ children }) => {
  const { cache: globalCache, mutate: globalMutate } = useSWRConfig()

  const { filterID } = useTaskCardState()
  const { tab: currentTab } = useTaskPageState()

  const [cache, setCache] = React.useState<CacheType>({
    [DASHBOARD_TAB]: {},
    [CATEGORY_TAB]: {},
    [ALL_TAB]: {}
  })

  const updateCardChange = React.useCallback(
    (oldTask: APITask, newTask: APITask) => {
      // Values that are null should be treated as false
      const oldVal = oldTask[filterID] ?? false
      const newVal = newTask[filterID] ?? false

      const oldColumnIDKey = `${filterID}s[]=${oldVal}`
      const newColumnIDKey = `${filterID}s[]=${newVal}`

      // @ts-ignore
      const toMutate = [...globalCache.entries()].filter(([key, data]) => {
        // Update columns
        const oldColumnMatches = key.includes(oldColumnIDKey)
        const newColumnMatches = key.includes(newColumnIDKey)
        return oldColumnMatches || newColumnMatches
      })

      // Trigger update on required keys
      toMutate.map(([key, _]) => globalMutate(key))
    },
    [filterID, globalMutate, globalCache]
  )

  // Updates columns and subtasks for card view
  const handleUpdateCards = React.useCallback(
    (task: APITask | null) => {
      if (!task) {
        return
      }

      // Only mutate the needed column and task
      const columnIDKey = `${filterID}s[]=${task[filterID]}`
      const subtaskKey = `subtasks:${task.parent_id}`

      // @ts-ignore
      const toMutate = [...globalCache.entries()].filter(([key, data]) => {
        const isInfiniteDataArray = Array.isArray(data)

        // SWR infinite hooks have an array as their data
        if (!isInfiniteDataArray) {
          return false
        }

        // Update columns and subtasks
        const columnMatches = key.includes(columnIDKey)
        const subtaskMatches = key.includes(subtaskKey)
        return columnMatches || subtaskMatches
      })

      // Trigger update on required keys
      toMutate.map(([key, _]) => globalMutate(key))
    },
    [filterID, globalCache, globalMutate]
  )

  const updateTasks: TaskSWRMutateCacheContextType['updateTasks'] =
    React.useCallback(
      (tab, task) => {
        const mutators = cache[tab]

        // Mutate all for dashboard and all tasks
        if ([DASHBOARD_TAB, ALL_TAB].includes(tab)) {
          Object.values(mutators).forEach(m => m())
          return
        }

        // Mutate specific category
        if (tab === CATEGORY_TAB) {
          const mutator = mutators[task.category_id ?? 'No Category']
          mutator && mutator()
          return
        }

        if (tab === CARDS_TAB) {
          handleUpdateCards(task)
          return
        }

        console.warn('Invalid task tab', tab)
      },
      [cache, handleUpdateCards]
    )

  const updateCategories: TaskSWRMutateCacheContextType['updateCategories'] =
    React.useCallback(() => {
      if (currentTab !== CATEGORY_TAB) {
        return
      }

      // Cause category caches to refresh
      const mutators = cache[CATEGORY_TAB]
      Object.values(mutators).forEach(m => m())
    }, [currentTab, cache])

  const setMutator: TaskSWRMutateCacheContextType['setMutator'] =
    React.useCallback((tab, key, mutator) => {
      setCache(old => {
        const newCache = { ...old }
        newCache[tab] = {
          ...newCache[tab],
          [key]: mutator
        }
        return newCache
      })
    }, [])

  return (
    <TaskSWRMutateCacheContext.Provider
      value={{ updateCardChange, updateTasks, updateCategories, setMutator }}
    >
      {children}
    </TaskSWRMutateCacheContext.Provider>
  )
}

export default TaskSWRMutateCacheProvider

export const useTaskSWRMutateCache = () =>
  React.useContext(TaskSWRMutateCacheContext)!
